import React, { useEffect, useState } from 'react';
import PlotTrackAddScene from './PlotTrackAddScene';
import PlotTrackCell from './PlotTrackCell';
import PlotTrackHeaderSpan from './PlotTrackHeaderSpan';
import { RowColorControl } from './PlotTrackGrid';
import SceneCardMenu from './SceneCardMenu';
import PlotTrackWhenCell from './PlotTrackWhenCell';
import {
  cellAt,
  cellsForColumn,
  columnDisplayName,
  columnIsSequence,
  rowCanPickColor,
  rowLabel,
} from '../../utils/plottrackGrid';
import {
  chapterOpeningAt,
  chaptersStartingAfterBreaks,
  dropBeforeScene,
  outlineParts,
  outsideSequenceHint,
  partExample,
  partLabel,
  previousSceneInReadingOrder,
  readingOrderSceneIds,
  sceneCardBlurb,
} from '../../utils/outlineCardRows';
import { visualPlace } from './sceneUndo';
import { clearPlotTrackDraft, readPlotTrackDraft, writePlotTrackDraft } from './plotTrackDraft';
import {
  clampWidth, columnWidthPx, widthStyle, WIDTH_MAX, WIDTH_MIN, WIDTH_STEP,
} from '../../utils/plottrackWidth';
import {
  ADD_SCENE_PLUS,
  CHAPTER_REMOVE_BREAK,
  CHAPTER_REMOVE_BREAK_ARIA,
  EDIT_SEQUENCE,
  OUTLINE_CARD_HINT,
  OUTLINE_EXAMPLE,
  OUTLINE_NOT_PLANNED,
  OUTLINE_PART,
  OUTLINE_PLAN_PART,
  PT_PEOPLE_MORE,
  PT_WIDTH_HANDLE_ARIA,
} from '../../utils/writerCopy';

function paperwhiteNow() {
  return document.documentElement.getAttribute('data-display-mode') === 'paperwhite';
}

function chipName(name) {
  const first = String(name || '').split('/')[0].replace(/\([^)]*\)/g, ' ').replace(/["“”]/g, ' ').trim();
  if (/^(the|a|an)\s/i.test(first)) return first.replace(/\s+/g, ' ');
  return first.split(/\s+/)[0] || String(name || '');
}

function SceneChips({ people }) {
  const list = people || [];
  if (list.length === 0) return null;
  const shown = list.slice(0, 3).map((person) => chipName(person.name));
  return (
    <span className="pt-grid__people" title={list.map((person) => chipName(person.name)).join(' · ')}>
      {shown.join(' · ')}
      {list.length > 3 ? ` ${PT_PEOPLE_MORE(list.length - 3)}` : ''}
    </span>
  );
}

function ResizeHandle({ sceneName, width, onDrag, onCommit }) {
  const startDrag = (event) => {
    if (paperwhiteNow()) return;
    const card = event.currentTarget.parentElement;
    const startX = event.clientX;
    const startWidth = width || card.getBoundingClientRect().width;
    let last = startWidth;
    const move = (e) => { last = clampWidth(startWidth + e.clientX - startX); onDrag(last); };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      onCommit(last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    event.preventDefault();
  };
  const onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const card = event.currentTarget.parentElement;
    const base = width || card.getBoundingClientRect().width || WIDTH_MIN;
    onCommit(clampWidth(base + (event.key === 'ArrowRight' ? WIDTH_STEP : -WIDTH_STEP)));
  };
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={PT_WIDTH_HANDLE_ARIA(sceneName)}
      aria-valuemin={WIDTH_MIN}
      aria-valuemax={WIDTH_MAX}
      aria-valuenow={width || undefined}
      tabIndex={0}
      className="pt-col-resize"
      onPointerDown={startDrag}
      onKeyDown={onKeyDown}
    />
  );
}

function openRow(rows) {
  return (rows || []).find((row) => row.type === 'main') || (rows || [])[0] || null;
}

export default function OutlineCardRows({
  rows,
  columns,
  lineColumns,
  cells,
  headers,
  onSelectCell,
  selected = null,
  onChangeRowColor,
  showWhen = false,
  whenLabels = [],
  onSetWhen,
  onCreateWhen,
  columnWidth = 'standard',
  onResizeColumn,
  onRenameScene,
  onMoveScene,
  onReorderScene,
  onStartChapter,
  onRemoveChapter,
  onChatScene,
  onPlanPart,
  onDeleteScene,
  onAddScene,
  onSuggestTitle,
  onEditHeader,
  onOpenScene,
  frameworkNames = [],
  frameworkApplied = false,
  storyId = '',
}) {
  const [dragPx, setDragPx] = useState({});
  const spanKey = storyId ? `span:${storyId}` : '';
  const [editingId, setEditingId] = useState(() => readPlotTrackDraft(spanKey)?.editingId || null);
  useEffect(() => {
    if (!spanKey) return undefined;
    if (editingId) writePlotTrackDraft(spanKey, { editingId });
    else clearPlotTrackDraft(spanKey);
    return undefined;
  }, [spanKey, editingId]);
  const parts = outlineParts({ columns, headers, frameworkNames });
  // Move to lists the parts the writer sees (framework names, scenes as shown),
  // so a part with no header name or scene_ids of its own is still listed.
  const sequences = headers?.sequences || [];
  const moveSequences = parts
    .filter((part) => part.sequence?.id)
    .map((part) => ({
      ...part.sequence,
      display_name: partLabel(part.sequence) || part.sequence.display_name,
      scene_ids: part.scenes.map((scene) => scene.scene_id),
    }));
  const sceneTitles = {};
  (columns || []).forEach((column) => {
    if (!column?.scene_id) return;
    sceneTitles[String(column.scene_id)] = columnDisplayName(column, cellsForColumn(column, cells));
  });
  const pickerColumns = lineColumns || columns;
  const row = openRow(rows);
  const storyRows = rows || [];
  const showCellStrip = storyRows.length > 1;
  const colorRows = storyRows.filter((item) => rowCanPickColor(item));
  const threadColor = (rows || []).find((item) => item.type === 'main')?.color
    || (rows || [])[0]?.color
    || 'var(--accent)';

  const pxFor = (column) => dragPx[column.id] ?? columnWidthPx(column, columnWidth);

  const selectScene = (column) => {
    if (!row || !onSelectCell) return;
    onSelectCell(row, column, cellAt(cells, row.id, column.id));
  };

  // #2263: the title opens Scene writing. The rest of the card selects
  // it and opens the side panel. The … button only opens the menu.
  const openScene = (column) => {
    if (onOpenScene) {
      onOpenScene(column);
      return;
    }
    selectScene(column);
  };

  const chapters = chaptersStartingAfterBreaks(
    headers?.chapters || [],
    readingOrderSceneIds(parts),
  );

  return (
    <div className="pt-card-rows">
      {colorRows.length > 0 && onChangeRowColor && (
        <div className="pt-row-colors">
          {colorRows.map((storyRow) => (
            <RowColorControl key={storyRow.id} row={storyRow} onChangeColor={onChangeRowColor} />
          ))}
        </div>
      )}
      {parts.map((part) => {
        const name = partLabel(part.sequence);
        const heading = name
          ? `${OUTLINE_PART(part.number, name)}${part.planned ? '' : ` · ${OUTLINE_NOT_PLANNED}`}`
          : '';
        const example = part.planned ? '' : partExample(part.sequence);
        const afterId = part.scenes[part.scenes.length - 1]?.scene_id;
        return (
          <section key={part.sequence?.id || `loose-${part.number}`} className="pt-card-rows__section">
            {onEditHeader && part.sequence?.id && String(editingId) === String(part.sequence.id) ? (
              <PlotTrackHeaderSpan
                kind="sequence"
                header={part.sequence}
                columns={pickerColumns}
                onSave={async (payload) => {
                  await onEditHeader(payload);
                  setEditingId(null);
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : heading && onEditHeader && part.sequence?.id ? (
              <button
                type="button"
                className="pt-card-rows__part pt-card-rows__edit"
                aria-label={`${EDIT_SEQUENCE}: ${part.sequence.display_name || heading}`}
                onClick={() => setEditingId(part.sequence.id)}
              >
                {heading}
              </button>
            ) : heading ? (
              <h2 className="pt-card-rows__part">{heading}</h2>
            ) : null}
            <div className="pt-card-rows__line">
              {part.scenes.map((column, index) => {
                const related = cellsForColumn(column, cells);
                const sequenceSized = columnIsSequence(column, related);
                const sceneName = columnDisplayName(column, related);
                const grainWord = sequenceSized ? 'sequence' : 'scene';
                const blurb = sceneCardBlurb(column, cells, rows);
                const mainCell = row ? cellAt(cells, row.id, column.id) : null;
                const mainAbsent = mainCell?.state === 'absent';
                const width = widthStyle(pxFor(column));
                const opening = chapterOpeningAt(chapters, column.scene_id);
                const chapterName = (opening?.display_name || '').trim();
                const outsideHint = outsideSequenceHint(
                  part,
                  frameworkApplied || frameworkNames.length > 0,
                  sceneName,
                );
                const previousSceneId = previousSceneInReadingOrder(parts, column.scene_id);
                return (
                  <React.Fragment key={column.id}>
                  {opening && (
                    <div className="pt-chapter-break" role="group" aria-label={chapterName || 'Chapter'}>
                      <span className="pt-chapter-break__rule" aria-hidden="true" />
                      <span className="pt-chapter-break__name">{chapterName || 'Chapter'}</span>
                      {onRemoveChapter && (
                        <button
                          type="button"
                          className="pt-chapter-break__remove"
                          aria-label={CHAPTER_REMOVE_BREAK_ARIA(chapterName || 'Chapter')}
                          onClick={() => onRemoveChapter(opening)}
                        >
                          {CHAPTER_REMOVE_BREAK}
                        </button>
                      )}
                      <span className="pt-chapter-break__rule" aria-hidden="true" />
                    </div>
                  )}
                  <article
                    className="pt-scene-card"
                    style={{ ...width, borderLeftColor: threadColor }}
                    onClick={(event) => {
                      const target = event.target;
                      if (target?.closest?.('.pt-scene-menu, .pt-col-resize, .pt-scene-card__open, .pt-cell, button, a, input, select, textarea')) {
                        return;
                      }
                      selectScene(column);
                    }}
                    draggable={Boolean(onReorderScene)}
                    onDragStart={(event) => {
                      if (paperwhiteNow() || !onReorderScene) {
                        event.preventDefault();
                        return;
                      }
                      event.dataTransfer.setData('text/plain', String(column.scene_id));
                      event.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(event) => {
                      if (!onReorderScene) return;
                      event.preventDefault();
                    }}
                    onDrop={(event) => {
                      if (!onReorderScene) return;
                      event.preventDefault();
                      const draggedId = event.dataTransfer.getData('text/plain');
                      const place = dropBeforeScene(part, draggedId, column.scene_id);
                      if (place) onReorderScene(draggedId, place, visualPlace(part, draggedId));
                    }}
                  >
                    <div className="pt-scene-card__top">
                      {column.scene_id && (
                        <SceneCardMenu
                          sceneName={sceneName}
                          sceneId={column.scene_id}
                          sequences={moveSequences}
                          titles={sceneTitles}
                          canStartChapter={Boolean(previousSceneId)}
                          offerStartChapter={!opening}
                          onOpen={() => openScene(column)}
                          onRename={onRenameScene
                            ? (title) => onRenameScene(column.scene_id, title)
                            : undefined}
                          onMove={onMoveScene
                            ? (place) => onMoveScene(column.scene_id, place)
                            : undefined}
                          onStartChapter={onStartChapter ? () => {
                            if (previousSceneId) onStartChapter(previousSceneId);
                          } : undefined}
                          onChat={onChatScene ? () => onChatScene(sceneName, column.scene_id) : undefined}
                          onDelete={onDeleteScene ? () => onDeleteScene(column.scene_id) : undefined}
                        />
                      )}
                      <button
                        type="button"
                        className="pt-scene-card__open"
                        aria-label={`Select ${grainWord} ${sceneName}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          openScene(column);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          openScene(column);
                        }}
                      >
                        <span className="pt-scene-card__title" title={`${part.number}.${index + 1} ${sceneName}`}>
                          {`${part.number}.${index + 1} ${sceneName}`}
                        </span>
                        {blurb && <span className="pt-scene-card__blurb">{blurb}</span>}
                      </button>
                      {outsideHint && <p className="pt-scene-card__outside">{outsideHint}</p>}
                    </div>
                    {showCellStrip && (
                      <div className="pt-scene-card__cells">
                        {storyRows.map((storyRow) => {
                          const storyCell = cellAt(cells, storyRow.id, column.id);
                          const picked = selected
                            && String(selected.row?.id) === String(storyRow.id)
                            && String(selected.column?.id) === String(column.id);
                          return (
                            <div key={storyRow.id} className="pt-scene-card__thread">
                              <span className="pt-scene-card__thread-name">{rowLabel(storyRow)}</span>
                              <PlotTrackCell
                                cell={storyCell}
                                row={storyRow}
                                column={column}
                                selected={Boolean(picked)}
                                onSelect={onSelectCell}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {!showCellStrip && mainAbsent && (
                      <PlotTrackCell
                        cell={mainCell}
                        row={row}
                        column={column}
                        selected={Boolean(
                          selected
                          && String(selected.row?.id) === String(row.id)
                          && String(selected.column?.id) === String(column.id),
                        )}
                        onSelect={onSelectCell}
                      />
                    )}
                    <SceneChips people={column.people} />
                    {showWhen && (
                      <PlotTrackWhenCell
                        sceneName={sceneName || `${index + 1}`}
                        value={column.when?.id || ''}
                        offer={column.when_offer}
                        labels={whenLabels}
                        onSet={(labelId) => onSetWhen && onSetWhen(column.scene_id, labelId)}
                        onCreate={(label) => onCreateWhen && onCreateWhen(label)}
                      />
                    )}
                    {onResizeColumn && (
                      <ResizeHandle
                        sceneName={sceneName}
                        width={pxFor(column)}
                        onDrag={(px) => setDragPx((prev) => ({ ...prev, [column.id]: px }))}
                        onCommit={(px) => {
                          setDragPx((prev) => {
                            const next = { ...prev };
                            delete next[column.id];
                            return next;
                          });
                          onResizeColumn(column.id, px);
                        }}
                      />
                    )}
                  </article>
                  </React.Fragment>
                );
              })}
              {!part.planned && (
                <article className="pt-scene-card pt-scene-card--example">
                  {example && <p>{OUTLINE_EXAMPLE(example)}</p>}
                  <button
                    type="button"
                    className="pt-toolbar__btn"
                    onClick={() => onPlanPart && onPlanPart(name || heading)}
                  >
                    {OUTLINE_PLAN_PART}
                  </button>
                </article>
              )}
              {onAddScene && (
                <article
                  className="pt-scene-card pt-scene-card--add"
                  onDragOver={(event) => {
                    if (!onReorderScene) return;
                    event.preventDefault();
                  }}
                  onDrop={(event) => {
                    if (!onReorderScene) return;
                    event.preventDefault();
                    const draggedId = event.dataTransfer.getData('text/plain');
                    const place = dropBeforeScene(part, draggedId, null);
                    if (place) onReorderScene(draggedId, place, visualPlace(part, draggedId));
                  }}
                >
                  <PlotTrackAddScene
                    idPrefix={`pt-add-${part.sequence?.id || 'outline'}`}
                    draftKey={storyId ? `add:${storyId}:${part.sequence?.id || part.number}` : ''}
                    buttonLabel={ADD_SCENE_PLUS}
                    partName={name}
                    columns={pickerColumns}
                    cells={cells}
                    sequences={sequences}
                    defaultAfterSceneId={afterId}
                    onAdd={onAddScene}
                    onSuggestTitle={onSuggestTitle}
                  />
                </article>
              )}
            </div>
          </section>
        );
      })}
      <p className="pt-card-rows__hint">{OUTLINE_CARD_HINT}</p>
    </div>
  );
}
